import { useId, useRef, type InputHTMLAttributes, type ReactNode } from 'react'
import { useFileDrop } from '../../lib/useFileDrop'
import { DateInput } from './DatePopover'

/**
 * A labelled input. The label is the redesign's field-label specimen — 600
 * 11px at .13em, uppercase, muted — which is a size down and a good deal wider
 * than the one the design system shipped; the input itself is unchanged, since
 * --color-chip and --color-border are already doing the right thing under both
 * themes.
 */

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string
  error?: string
  /** A muted line under the input — a format hint, a consequence, an example. */
  note?: ReactNode
  /** For a field laid out as a table column, where a heading row labels it instead. */
  hideLabel?: boolean
}

export function Field({
  label,
  id,
  error,
  note,
  hideLabel = false,
  className = '',
  style,
  ...props
}: FieldProps) {
  // Every file upload in Wingspace takes a drop, so a `<Field type="file">`
  // gets one without each caller remembering to ask.
  const fileRef = useRef<HTMLInputElement>(null)
  const isFile = props.type === 'file'
  const { dragging, dropProps } = useFileDrop({ inputRef: fileRef, disabled: !isFile || props.disabled })
  const generatedId = useId()
  const inputId = id ?? generatedId

  // `type="date"` is the app's own calendar rather than the browser's — see
  // components/ui/DatePopover.tsx. Switched here rather than at each call site
  // so every screen changes together: a product with two kinds of date picker
  // in it is worse than either kind.
  const isDate = props.type === 'date'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <label
        htmlFor={inputId}
        className={hideLabel ? 'sr-only' : undefined}
        style={{
          font: '600 11px Manrope,sans-serif',
          letterSpacing: '.13em',
          textTransform: 'uppercase',
          color: 'var(--color-muted)',
        }}
      >
        {label}
      </label>
      {/* The sunk ground the handoff gives every field — §3's search spec is the
          same control: radius 8, 1px --w-line, --w-sunk. This was bg-fill on
          border-line, which are the surfaces the redesign replaced, so a
          Field sat on the old palette next to every other input in the shell.
          Inline rather than Tailwind classes, for the same reason the rest of
          this set is: a class list is how it drifted back the first time. */}
      {isDate ? (
        <DateInput
          name={props.name}
          value={props.value}
          defaultValue={props.defaultValue}
          onChange={props.onChange}
          required={props.required}
          ariaLabel={props['aria-label'] ?? label}
          placeholder={props.placeholder}
          style={{
            padding: '8px 11px',
            borderRadius: 8,
            border: `1px solid ${error ? 'var(--w-danger-line)' : 'var(--w-line)'}`,
            background: 'var(--w-sunk)',
            font: '500 14px Manrope,sans-serif',
            minWidth: 0,
            ...style,
          }}
        />
      ) : (
      <input
        id={inputId}
        ref={isFile ? fileRef : undefined}
        aria-invalid={error ? true : undefined}
        {...(isFile ? dropProps : {})}
        className={`font-body text-sm focus:outline-none ${className}`}
        style={{
          padding: '8px 11px',
          borderRadius: 8,
          border: `1px solid ${
            error
              ? 'var(--w-danger-line)'
              : dragging
                ? 'var(--w-attention-line)'
                : 'var(--w-line)'
          }`,
          background: dragging ? 'var(--w-pane-hi)' : 'var(--w-sunk)',
          color: 'var(--color-text)',
          minWidth: 0,
          transition: 'border-color .15s ease, background .15s ease',
          ...style,
        }}
        onFocus={(event) => {
          if (!error) event.currentTarget.style.borderColor = 'var(--color-action-ink)'
          props.onFocus?.(event)
        }}
        onBlur={(event) => {
          if (!error) event.currentTarget.style.borderColor = 'var(--w-line)'
          props.onBlur?.(event)
        }}
        {...props}
      />
      )}
      {isFile && !error && (
        <span style={{ font: '400 11px/1.6 Manrope,sans-serif', color: 'var(--color-faint)' }}>
          {dragging ? 'Drop it here.' : 'Drop a file here, or choose one.'}
        </span>
      )}
      {note && !error && (
        <span style={{ font: '400 11px/1.6 Manrope,sans-serif', color: 'var(--color-muted)' }}>{note}</span>
      )}
      {error && (
        <span style={{ font: '400 11px/1.6 Manrope,sans-serif', color: 'var(--w-danger-ink)' }}>{error}</span>
      )}
    </div>
  )
}
